/**
 * Calibration-focused human vs Ace analysis — find disparity patterns and re-review
 * cases to tune how Ace rates.
 */

import { useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronDown,
  faChevronRight,
  faFileExcel,
  faLightbulb,
  faRobot,
  faScaleBalanced,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { Button } from "@/components/ui/button/Button";
import {
  agentReviewAPI,
  type BatchAceRateResult,
  type CalibrationPriority,
  type CalibrationResolutionOutcome,
  type CheckDisagreementDetail,
  type ReviewAnalysisResult,
} from "@/services/api/agentReview";
import {
  CalibrationCheckDisagreementsSection,
  CalibrationMisalignedReviewsSection,
  HumanRagOverview,
} from "./CalibrationFeedbackSections";
import { CalibrationResolutionControls, outcomeLabel } from "./CalibrationResolutionControls";
import {
  disagreementForCase,
  disagreementsForPriority,
  disputeResolutionKey,
  humanCommentSamples,
  isArtefactPhase,
  phaseLabel,
  priorityBulkResolvedSummary,
  priorityResolutionKey,
  representativeDispute,
} from "./calibrationAnalysisHelpers";
import { ArtefactCalibrationDrawer } from "./ArtefactCalibrationDrawer";
import { CriteriaVersionsPanel } from "./CriteriaVersionsPanel";
import { ragOption } from "./RagOption";
import { type ReviewCaseOpenIntent, viewSubmittedCaseIntent } from "./reviewCaseOpenIntent";
import type { Rag } from "./types";

interface AgentReviewAnalysisPanelProps {
  analysis: ReviewAnalysisResult | null;
  loading: boolean;
  error: string | null;
  catalogueKey?: string | null;
  sessionId?: string | null;
  onOpenCase: (runId: string, label?: string, intent?: ReviewCaseOpenIntent) => void;
  onRefresh: () => void;
  onExportCalibration?: () => void;
  exportingCalibration?: boolean;
}

function pct(agreed: number, compared: number): string {
  if (compared === 0) return "—";
  return `${Math.round((agreed / compared) * 100)}%`;
}

function pendingAceLabel(pendingCount: number, running: boolean): string {
  const noun = pendingCount === 1 ? "case" : "cases";
  if (running) {
    return `Running Ace on ${pendingCount} reviewed ${noun}…`;
  }
  return `Run Ace on ${pendingCount} reviewed ${noun}`;
}

function BatchAceRateSummary({ result }: { result: BatchAceRateResult }) {
  if (result.rated_count === 0 && result.failed_count === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        "rounded-lg px-3 py-2 text-xs ring-1",
        result.failed_count > 0
          ? "bg-amber-500/[0.08] text-amber-100 ring-amber-500/20"
          : "bg-emerald-500/[0.08] text-emerald-100 ring-emerald-500/20",
      )}
      data-testid="agent-review-batch-ace-result"
    >
      <p>
        Ace rated {result.rated_count} of {result.pending_count} pending{" "}
        {result.pending_count === 1 ? "case" : "cases"}.
        {result.failed_count > 0 ? ` ${result.failed_count} failed.` : ""}
      </p>
      {result.failures.length > 0 ? (
        <ul className="mt-2 space-y-1 text-amber-200/90">
          {result.failures.slice(0, 3).map((failure) => (
            <li key={failure.review_id}>
              {failure.subject_label || failure.run_id}: {failure.error}
            </li>
          ))}
          {result.failures.length > 3 ? (
            <li>+{result.failures.length - 3} more failures</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function RunPendingAceButton({
  pendingCount,
  running,
  onRun,
}: {
  pendingCount: number;
  running: boolean;
  onRun: () => void;
}) {
  if (pendingCount <= 0) {
    return null;
  }

  return (
    <Button
      color="indigo"
      disabled={running}
      onClick={onRun}
      data-testid="agent-review-rate-pending-ace"
    >
      {pendingAceLabel(pendingCount, running)}
    </Button>
  );
}

function priorityKey(priority: CalibrationPriority): string {
  return `${priority.phase}:${priority.check_id}`;
}

function RagPill({ rag }: { rag: Rag }) {
  const opt = ragOption(rag);
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1",
        rag === "green" && "bg-emerald-500/10 text-emerald-200 ring-emerald-500/25",
        rag === "amber" && "bg-amber-500/10 text-amber-200 ring-amber-500/25",
        rag === "red" && "bg-rose-500/10 text-rose-200 ring-rose-500/25",
      )}
    >
      {opt.label}
    </span>
  );
}

function CalibrationPriorityCard({
  priority,
  expanded,
  onToggle,
  onOpenCase,
  onCompareArtefact,
  checkDisagreements,
  savingResolutionKey,
  onResolveBulkClosed,
  onClearBulkClosed,
  onResolveCase,
  onClearCase,
}: {
  priority: CalibrationPriority;
  expanded: boolean;
  onToggle: () => void;
  onOpenCase: (runId: string, label?: string, intent?: ReviewCaseOpenIntent) => void;
  onCompareArtefact?: (item: CheckDisagreementDetail) => void;
  checkDisagreements: CheckDisagreementDetail[];
  savingResolutionKey: string | null;
  onResolveBulkClosed: (priority: CalibrationPriority, note: string) => void;
  onClearBulkClosed: (priority: CalibrationPriority) => void;
  onResolveCase: (
    item: CheckDisagreementDetail,
    outcome: Extract<CalibrationResolutionOutcome, "human_correct" | "ace_correct">,
    note: string,
  ) => void;
  onClearCase: (item: CheckDisagreementDetail) => void;
}) {
  const humanSamples = humanCommentSamples(priority, checkDisagreements);
  const bulkResolvedSummary = priorityBulkResolvedSummary(priority, checkDisagreements);
  const representative = representativeDispute(priority, checkDisagreements);
  const bulkResolutionItem =
    representative && bulkResolvedSummary.status === "all_closed"
      ? {
          ...representative,
          resolution_outcome: "resolved" as const,
          resolution_note: bulkResolvedSummary.note,
        }
      : representative;
  const savingBulk = savingResolutionKey === priorityResolutionKey(priority);
  const fullyResolved =
    priority.dispute_count > 0 &&
    (priority.open_dispute_count ?? 0) === 0 &&
    (priority.resolved_dispute_count ?? 0) === priority.dispute_count;

  return (
    <div
      className={clsx(
        "rounded-xl ring-1 overflow-hidden",
        fullyResolved
          ? "ring-emerald-500/25 bg-emerald-500/[0.06]"
          : "ring-amber-500/20 bg-amber-500/[0.04]",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.02]"
      >
        <FontAwesomeIcon
          icon={expanded ? faChevronDown : faChevronRight}
          className="mt-1 text-slate-500 text-xs shrink-0"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {fullyResolved ? (
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-200 ring-1 ring-emerald-500/30 tabular-nums">
                {priority.dispute_count} resolved
              </span>
            ) : (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-200 ring-1 ring-amber-500/25 tabular-nums">
                {(priority.open_dispute_count ?? priority.dispute_count) > 0
                  ? `${priority.open_dispute_count ?? priority.dispute_count} open`
                  : `${priority.dispute_count} ${priority.dispute_count === 1 ? "case" : "cases"}`}
              </span>
            )}
            {!fullyResolved && (priority.resolved_dispute_count ?? 0) > 0 ? (
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-200 ring-1 ring-emerald-500/20 tabular-nums">
                {priority.resolved_dispute_count} resolved
              </span>
            ) : null}
            <span className="text-[11px] text-slate-500">{phaseLabel(priority.phase)}</span>
            {priority.severity ? (
              <span className="text-[11px] text-slate-600">{priority.severity} check</span>
            ) : null}
          </div>
          <p className="text-sm font-medium text-slate-100">{priority.check_label}</p>
          <p
            className={clsx(
              "text-xs",
              fullyResolved ? "text-emerald-200/90" : "text-amber-200/90",
            )}
          >
            {priority.dominant_pattern}
          </p>
        </div>
      </button>

      {expanded ? (
        <div
          className={clsx(
            "px-4 py-4 space-y-4 border-t",
            fullyResolved ? "border-emerald-500/20" : "border-amber-500/15",
          )}
        >
          <div className="rounded-lg bg-indigo-500/[0.08] px-3 py-3 ring-1 ring-indigo-500/20">
            <p className="text-[11px] font-medium uppercase tracking-wide text-indigo-300 mb-1.5 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faLightbulb} className="text-xs" />
              Calibration action
            </p>
            <p className="text-sm text-slate-200 leading-relaxed">{priority.calibration_hint}</p>
          </div>

          {priority.answer_pairs.length > 1 ? (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
                Answer patterns
              </p>
              <ul className="space-y-1">
                {priority.answer_pairs.map((pair) => (
                  <li key={`${pair.human_answer}-${pair.ace_answer}`} className="text-xs text-slate-400">
                    Human {pair.human_answer} · Ace {pair.ace_answer}
                    <span className="text-slate-500"> — {pair.count} cases</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {humanSamples.length > 0 ? (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
                What humans said (samples)
              </p>
              <ul className="space-y-2">
                {humanSamples.map((comment, i) => (
                  <li key={i} className="text-xs text-slate-300 leading-relaxed pl-3 border-l-2 border-slate-500">
                    {comment}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {priority.ace_reason_samples.length > 0 ? (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
                What Ace said (samples)
              </p>
              <ul className="space-y-2">
                {priority.ace_reason_samples.map((reason, i) => (
                  <li key={i} className="text-xs text-slate-400 leading-relaxed pl-3 border-l-2 border-indigo-500/40">
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {bulkResolutionItem ? (
            <div className="rounded-lg bg-white/[0.03] ring-1 ring-white/5 px-3 py-3">
              <CalibrationResolutionControls
                item={bulkResolutionItem}
                saving={savingBulk}
                caseCount={priority.dispute_count}
                allowedOutcomes={["resolved"]}
                bulkResolvedSummary={bulkResolvedSummary.status}
                onResolve={(_outcome, note) => onResolveBulkClosed(priority, note)}
                onClear={() => onClearBulkClosed(priority)}
              />
            </div>
          ) : null}

          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
              Affected cases
            </p>
            <ul className="space-y-3">
              {priority.affected_cases.map((item) => {
                const detail = disagreementForCase(
                  priority,
                  item.review_id,
                  checkDisagreements,
                  item.artifact_id,
                );
                const caseLabel = item.artifact_title?.trim() || item.subject_label || "Case";
                return (
                  <li
                    key={`${item.review_id}:${item.artifact_id ?? ""}`}
                    className="rounded-lg bg-white/[0.03] ring-1 ring-white/5 overflow-hidden"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 px-3 py-2.5">
                      <div className="min-w-0 flex items-center gap-2">
                        <p className="text-sm text-slate-200 truncate">{caseLabel}</p>
                        {detail?.resolution_outcome ? (
                          <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-200 ring-1 ring-emerald-500/20">
                            {outcomeLabel(detail.resolution_outcome)}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {detail && isArtefactPhase(priority.phase) && onCompareArtefact ? (
                          <button
                            type="button"
                            onClick={() => onCompareArtefact(detail)}
                            className="text-xs text-indigo-300 hover:text-indigo-200"
                          >
                            Compare
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() =>
                            onOpenCase(
                              item.run_id,
                              item.subject_label || undefined,
                              detail ? viewSubmittedCaseIntent(detail) : { viewSubmitted: true },
                            )
                          }
                          className="shrink-0 text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"
                        >
                          View case
                          <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
                        </button>
                      </div>
                    </div>

                    {detail ? (
                      <>
                        <div className="grid gap-2 px-3 py-2.5 sm:grid-cols-2 text-xs">
                          {detail.human_comment?.trim() ? (
                            <p className="text-slate-400 leading-relaxed">
                              <span className="text-slate-500">Human: </span>
                              {detail.human_comment}
                            </p>
                          ) : null}
                          {detail.ace_reason?.trim() ? (
                            <p className="text-slate-500 leading-relaxed">
                              <span className="text-indigo-400/80">Ace: </span>
                              {detail.ace_reason}
                            </p>
                          ) : null}
                        </div>
                        {detail.resolution_outcome !== "resolved" ? (
                          <div className="border-t border-white/5 px-3 py-3">
                            <CalibrationResolutionControls
                              item={detail}
                              saving={savingResolutionKey === disputeResolutionKey(detail)}
                              allowedOutcomes={["human_correct", "ace_correct"]}
                              onResolve={(outcome, note) =>
                                onResolveCase(
                                  detail,
                                  outcome as Extract<
                                    CalibrationResolutionOutcome,
                                    "human_correct" | "ace_correct"
                                  >,
                                  note,
                                )
                              }
                              onClear={() => onClearCase(detail)}
                            />
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function AgentReviewAnalysisPanel({
  analysis,
  loading,
  error,
  catalogueKey = null,
  sessionId = null,
  onOpenCase,
  onRefresh,
  onExportCalibration,
  exportingCalibration = false,
}: AgentReviewAnalysisPanelProps) {
  const [expandedPriority, setExpandedPriority] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchResult, setBatchResult] = useState<BatchAceRateResult | null>(null);
  const [resolutionError, setResolutionError] = useState<string | null>(null);
  const [savingResolutionKey, setSavingResolutionKey] = useState<string | null>(null);
  const [artefactCompareDispute, setArtefactCompareDispute] = useState<CheckDisagreementDetail | null>(
    null,
  );

  const resolutionPayload = (item: CheckDisagreementDetail) => ({
    catalogue_key: catalogueKey!,
    session_id: sessionId,
    review_id: item.review_id,
    run_id: item.run_id,
    phase: item.phase,
    check_id: item.check_id,
    check_label: item.check_label,
    artifact_id: item.artifact_id,
    human_answer: item.human_answer,
    ace_answer: item.ace_answer,
  });

  const handleResolveBulkClosed = async (priority: CalibrationPriority, note: string) => {
    if (!catalogueKey || !analysis) return;
    const items = disagreementsForPriority(priority, analysis.check_disagreements);
    if (items.length === 0) return;

    setSavingResolutionKey(priorityResolutionKey(priority));
    setResolutionError(null);
    try {
      await Promise.all(
        items.map((item) =>
          agentReviewAPI.resolveCalibrationDispute({
            ...resolutionPayload(item),
            outcome: "resolved",
            note,
          }),
        ),
      );
      onRefresh();
    } catch (err) {
      setResolutionError(err instanceof Error ? err.message : "Couldn't save resolution.");
    } finally {
      setSavingResolutionKey(null);
    }
  };

  const handleClearBulkClosed = async (priority: CalibrationPriority) => {
    if (!analysis) return;
    const items = disagreementsForPriority(priority, analysis.check_disagreements).filter(
      (item) => item.resolution_outcome === "resolved",
    );
    if (items.length === 0) return;

    setSavingResolutionKey(priorityResolutionKey(priority));
    setResolutionError(null);
    try {
      await Promise.all(
        items.map((item) =>
          agentReviewAPI.clearCalibrationResolution({
            review_id: item.review_id,
            phase: item.phase,
            check_id: item.check_id,
            artifact_id: item.artifact_id,
          }),
        ),
      );
      onRefresh();
    } catch (err) {
      setResolutionError(err instanceof Error ? err.message : "Couldn't re-open dispute.");
    } finally {
      setSavingResolutionKey(null);
    }
  };

  const handleResolveCase = async (
    item: CheckDisagreementDetail,
    outcome: CalibrationResolutionOutcome,
    note: string,
  ) => {
    if (!catalogueKey) return;
    setSavingResolutionKey(disputeResolutionKey(item));
    setResolutionError(null);
    try {
      await agentReviewAPI.resolveCalibrationDispute({
        ...resolutionPayload(item),
        outcome,
        note,
      });
      onRefresh();
    } catch (err) {
      setResolutionError(err instanceof Error ? err.message : "Couldn't save resolution.");
    } finally {
      setSavingResolutionKey(null);
    }
  };

  const handleClearCase = async (item: CheckDisagreementDetail) => {
    setSavingResolutionKey(disputeResolutionKey(item));
    setResolutionError(null);
    try {
      await agentReviewAPI.clearCalibrationResolution({
        review_id: item.review_id,
        phase: item.phase,
        check_id: item.check_id,
        artifact_id: item.artifact_id,
      });
      onRefresh();
    } catch (err) {
      setResolutionError(err instanceof Error ? err.message : "Couldn't re-open dispute.");
    } finally {
      setSavingResolutionKey(null);
    }
  };

  const handleRatePendingAce = async () => {
    if (!catalogueKey || !analysis || analysis.pending_ace_count <= 0) {
      return;
    }
    setBatchRunning(true);
    setBatchError(null);
    setBatchResult(null);
    try {
      const result = await agentReviewAPI.ratePendingWithAce(
        catalogueKey,
        sessionId,
        analysis.pending_ace_count,
      );
      setBatchResult(result);
      onRefresh();
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : "Couldn't run Ace on pending reviews.");
    } finally {
      setBatchRunning(false);
    }
  };

  const phasePriorities = useMemo(
    () => (analysis?.calibration_priorities ?? []).filter((priority) => !isArtefactPhase(priority.phase)),
    [analysis],
  );
  const artefactPriorities = useMemo(
    () => (analysis?.calibration_priorities ?? []).filter((priority) => isArtefactPhase(priority.phase)),
    [analysis],
  );

  const hasGaps = useMemo(() => {
    if (!analysis) return false;
    return (
      analysis.calibration_priorities.length > 0 ||
      analysis.phase_rag_disparities.length > 0 ||
      analysis.misaligned_reviews.length > 0
    );
  }, [analysis]);

  if (loading && !analysis) {
    return (
      <div className="space-y-3">
        <LoadingPlaceholder height="72px" />
        <LoadingPlaceholder height="160px" />
        <LoadingPlaceholder height="200px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-300">
        <p className="text-sm text-rose-400">{error}</p>
        <Button onClick={onRefresh}>Retry</Button>
      </div>
    );
  }

  if (!analysis) {
    return <p className="text-sm text-slate-500 py-8 text-center">No analysis data.</p>;
  }

  if (analysis.total_reviews === 0) {
    return (
      <p className="text-sm text-slate-500 py-8 text-center">
        No submitted reviews for this task type yet.
      </p>
    );
  }

  if (analysis.ace_rated_count === 0) {
    return (
      <div className="rounded-xl bg-white/[0.03] px-4 py-8 ring-1 ring-white/5 text-center space-y-4">
        <FontAwesomeIcon icon={faRobot} className="text-indigo-300 text-xl" />
        <div className="space-y-2">
          <p className="text-sm text-slate-200">
            {analysis.pending_ace_count} reviewed {analysis.pending_ace_count === 1 ? "case" : "cases"}{" "}
            without Ace ratings
          </p>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            Run Ace on reviewed cases first — calibration analysis needs both human and Ace ratings to
            compare.
          </p>
        </div>
        <RunPendingAceButton
          pendingCount={analysis.pending_ace_count}
          running={batchRunning}
          onRun={() => void handleRatePendingAce()}
        />
        {batchError ? <p className="text-sm text-rose-400">{batchError}</p> : null}
        {batchResult ? <BatchAceRateSummary result={batchResult} /> : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-4 pt-1">
      <CriteriaVersionsPanel catalogueKey={catalogueKey} />

      <div className="rounded-xl bg-indigo-500/[0.08] px-4 py-4 ring-1 ring-indigo-500/20 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <FontAwesomeIcon icon={faScaleBalanced} className="text-indigo-300 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-100">Calibrate Ace from human reviews</p>
              <p className="text-xs text-slate-400 leading-relaxed">
                Use human ratings as ground truth. Resolve disputed checks inline, then update Ace check
                guidance where humans are consistently right.
              </p>
            </div>
          </div>
          {onExportCalibration ? (
            <Button
              color="indigo"
              disabled={exportingCalibration || analysis.ace_rated_count === 0}
              onClick={onExportCalibration}
              data-testid="calibration-export-button"
            >
              <FontAwesomeIcon icon={faFileExcel} />
              {exportingCalibration ? "Exporting…" : "Export calibration"}
            </Button>
          ) : null}
        </div>
        <p className="text-sm text-slate-200 leading-relaxed border-t border-indigo-500/15 pt-3">
          {analysis.calibration_headline}
        </p>
        <div className="flex flex-wrap gap-4 text-xs text-slate-500 pt-1">
          <span>
            {analysis.ace_rated_count} Ace-rated · {analysis.pending_ace_count} pending Ace
          </span>
          {analysis.open_dispute_count > 0 ? (
            <span className="text-amber-200/90">{analysis.open_dispute_count} open disputes</span>
          ) : null}
          {analysis.resolved_dispute_count > 0 ? (
            <span className="text-emerald-200/90">{analysis.resolved_dispute_count} resolved</span>
          ) : null}
          <span>
            Orchestrate {pct(analysis.orchestration_agreement.agreed, analysis.orchestration_agreement.compared)} aligned
          </span>
          <span>
            Output {pct(analysis.output_agreement.agreed, analysis.output_agreement.compared)} aligned
          </span>
          {analysis.artefact_agreement?.compared ? (
            <span>
              Artefacts {pct(analysis.artefact_agreement.agreed, analysis.artefact_agreement.compared)} aligned
            </span>
          ) : null}
        </div>
        {analysis.pending_ace_count > 0 ? (
          <div className="flex flex-col gap-2 border-t border-indigo-500/15 pt-3">
            <RunPendingAceButton
              pendingCount={analysis.pending_ace_count}
              running={batchRunning}
              onRun={() => void handleRatePendingAce()}
            />
            {batchError ? <p className="text-sm text-rose-400">{batchError}</p> : null}
            {batchResult ? <BatchAceRateSummary result={batchResult} /> : null}
          </div>
        ) : null}
      </div>

      {!hasGaps && (analysis.open_dispute_count ?? 0) === 0 ? (
        <div className="rounded-xl bg-emerald-500/[0.06] px-4 py-6 ring-1 ring-emerald-500/20 text-center">
          <p className="text-sm text-emerald-200">No open calibration disputes</p>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Human and Ace agree on all rated cases. Keep adding reviews and Ace ratings to surface
            patterns as the test set grows.
          </p>
        </div>
      ) : null}

      {phasePriorities.length > 0 ? (
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-medium text-slate-200">Priority check disparities</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Most common gaps between human and Ace on orchestrate and output checks
            </p>
          </div>
          {phasePriorities.map((priority) => {
            const key = priorityKey(priority);
            return (
              <CalibrationPriorityCard
                key={key}
                priority={priority}
                expanded={expandedPriority === key}
                onToggle={() => setExpandedPriority((current) => (current === key ? null : key))}
                onOpenCase={onOpenCase}
                checkDisagreements={analysis.check_disagreements}
                savingResolutionKey={savingResolutionKey}
                onResolveBulkClosed={(priority, note) => void handleResolveBulkClosed(priority, note)}
                onClearBulkClosed={(priority) => void handleClearBulkClosed(priority)}
                onResolveCase={(item, outcome, note) => void handleResolveCase(item, outcome, note)}
                onClearCase={(item) => void handleClearCase(item)}
              />
            );
          })}
        </section>
      ) : null}

      {artefactPriorities.length > 0 ? (
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-medium text-slate-200">Artefact check disparities</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Gaps on generated artefacts — compare the letter and resolve who is right
            </p>
          </div>
          {artefactPriorities.map((priority) => {
            const key = priorityKey(priority);
            return (
              <CalibrationPriorityCard
                key={key}
                priority={priority}
                expanded={expandedPriority === key}
                onToggle={() => setExpandedPriority((current) => (current === key ? null : key))}
                onOpenCase={onOpenCase}
                onCompareArtefact={(item) => setArtefactCompareDispute(item)}
                checkDisagreements={analysis.check_disagreements}
                savingResolutionKey={savingResolutionKey}
                onResolveBulkClosed={(priority, note) => void handleResolveBulkClosed(priority, note)}
                onClearBulkClosed={(priority) => void handleClearBulkClosed(priority)}
                onResolveCase={(item, outcome, note) => void handleResolveCase(item, outcome, note)}
                onClearCase={(item) => void handleClearCase(item)}
              />
            );
          })}
        </section>
      ) : null}

      {resolutionError ? <p className="text-sm text-rose-400">{resolutionError}</p> : null}

      <CalibrationCheckDisagreementsSection
        disagreements={analysis.check_disagreements}
        onOpenCase={onOpenCase}
        onCompareArtefact={(item) => setArtefactCompareDispute(item)}
      />

      <HumanRagOverview
        humanOrchRags={analysis.human_orch_rags}
        humanOutRags={analysis.human_out_rags}
      />

      <CalibrationMisalignedReviewsSection
        items={analysis.misaligned_reviews}
        onOpenCase={onOpenCase}
      />

      {analysis.phase_rag_disparities.length > 0 ? (
        <section className="rounded-xl bg-white/[0.03] ring-1 ring-white/5 overflow-hidden">
          <div className="border-b border-white/5 px-4 py-3">
            <h3 className="text-sm font-medium text-slate-200">Phase RAG gaps</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Overall orchestrate/output ratings where humans and Ace pick different RAGs
            </p>
          </div>
          <ul className="divide-y divide-white/5">
            {analysis.phase_rag_disparities.map((item) => (
              <li key={`${item.phase}-${item.human_rag}-${item.ace_rag}`} className="px-4 py-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-slate-500 capitalize">{item.phase}</span>
                  <span className="text-slate-600">·</span>
                  <span className="text-slate-400">Human</span>
                  <RagPill rag={item.human_rag} />
                  <span className="text-slate-600">vs Ace</span>
                  <RagPill rag={item.ace_rag} />
                  <span className="text-xs text-amber-200/90 tabular-nums">({item.count} cases)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.affected_cases.slice(0, 5).map((caseRef) => (
                    <button
                      key={caseRef.review_id}
                      type="button"
                      onClick={() =>
                        onOpenCase(caseRef.run_id, caseRef.subject_label || undefined, {
                          viewSubmitted: true,
                        })
                      }
                      className="text-xs text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline"
                    >
                      {caseRef.subject_label || "Case"}
                    </button>
                  ))}
                  {item.affected_cases.length > 5 ? (
                    <span className="text-xs text-slate-500">+{item.affected_cases.length - 5} more</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ArtefactCalibrationDrawer
        open={Boolean(artefactCompareDispute)}
        dispute={artefactCompareDispute}
        saving={
          artefactCompareDispute
            ? savingResolutionKey === disputeResolutionKey(artefactCompareDispute)
            : false
        }
        onClose={() => setArtefactCompareDispute(null)}
        onResolve={(item, outcome, note) => {
          void handleResolveCase(item, outcome, note).then(() => setArtefactCompareDispute(null));
        }}
        onClear={(item) => {
          void handleClearCase(item);
        }}
        onOpenCase={(runId, label, intent) => {
          setArtefactCompareDispute(null);
          onOpenCase(runId, label, intent);
        }}
      />
    </div>
  );
}
